import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Modal de creación/edición de una bandeja (tray).
 * - Sin `tray` prop → modo creación.
 * - Con `tray` prop → modo edición (precarga los campos y muestra "Eliminar").
 * @param {Object} props.tray - Bandeja a editar (null para crear)
 * @param {Function} props.onSave - Recibe los datos del formulario
 * @param {Function} props.onDelete - Solo en edición, abre la confirmación de borrado
 */
const CreateTrayModal = ({ open, onClose, onSave, onDelete, tray }) => {
  const isEdit = Boolean(tray);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [strategy, setStrategy] = useState('round_robin');
  const [maxContacts, setMaxContacts] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [botEnabled, setBotEnabled] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (tray) {
      setName(tray.name || '');
      setDescription(tray.description || '');
      setStrategy(tray.strategy || 'round_robin');
      setMaxContacts(tray.max_contacts != null ? String(tray.max_contacts) : '');
      setIsDefault(Boolean(tray.is_default));
      setBotEnabled(tray.bot_enabled != null ? Boolean(tray.bot_enabled) : true);
    } else {
      setName('');
      setDescription('');
      setStrategy('round_robin');
      setMaxContacts('');
      setIsDefault(false);
      setBotEnabled(true);
    }
    setError(null);
  }, [open, tray]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data = {
        name,
        description: description.trim() || undefined,
        strategy,
        max_contacts: maxContacts === '' ? null : Number(maxContacts),
        is_default: isDefault,
        bot_enabled: botEnabled,
      };
      await onSave(data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Error al guardar la bandeja');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Editar Bandeja' : 'Crear Nueva Bandeja'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Nombre de la bandeja"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />

          <TextField
            margin="dense"
            name="description"
            label="Descripción (opcional)"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
          />

          <Box sx={{ display: 'flex', gap: 3, mt: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  color="primary"
                />
              }
              label="Bandeja por defecto"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={botEnabled}
                  onChange={(e) => setBotEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Bot habilitado"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {isEdit && onDelete && (
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={onDelete}
                disabled={loading}
              >
                Eliminar
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !name.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {isEdit ? 'Guardar' : 'Crear'}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateTrayModal;
